import { useState, useEffect, useRef } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function AdminSupport() {
  const { user } = useApp();
  const { t } = useI18n();
  const [chats, setChats] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcast, setBroadcast] = useState({ title: '', message: '', target_role: '' });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadChats();
    const interval = setInterval(loadChats, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedUser) loadChat(selectedUser.user_id);
  }, [selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadChats() {
    try {
      const { data } = await api.get('/admin/support/chats');
      setChats(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function loadChat(userId) {
    try {
      const { data } = await api.get(`/admin/support/chat/${userId}`);
      setMessages(data || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function sendReply() {
    if (!newMessage.trim() || !selectedUser) return;
    setSending(true);
    try {
      await api.post('/admin/support/reply', {
        user_id: selectedUser.user_id,
        message: newMessage.trim(),
      });
      setNewMessage('');
      await loadChat(selectedUser.user_id);
      await loadChats();
    } catch (e) {
      alert(e.userMessage || 'Ошибка');
    } finally {
      setSending(false);
    }
  }

  async function sendBroadcast() {
    if (!broadcast.title.trim() || !broadcast.message.trim()) {
      alert('Заполните заголовок и сообщение');
      return;
    }
    try {
      await api.post('/admin/support/broadcast', {
        title: broadcast.title.trim(),
        message: broadcast.message.trim(),
        target_role: broadcast.target_role || null,
      });
      alert('✅ Рассылка отправлена!');
      setBroadcast({ title: '', message: '', target_role: '' });
      setShowBroadcast(false);
    } catch (e) {
      alert(e.userMessage || 'Ошибка');
    }
  }

  function formatTime(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  const isSuperadmin = user?.role === 'superadmin';

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100%', paddingBottom: 0 }}>
      <BackButton />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>📞 Поддержка</h1>
        {isSuperadmin && (
          <button className="btn btn-primary" onClick={() => setShowBroadcast(true)}>
            📢 Рассылка
          </button>
        )}
      </div>

      {/* Broadcast Modal */}
      {showBroadcast && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            background: '#1a1a2e', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 400,
          }}>
            <h3 style={{ marginTop: 0 }}>📢 Новая рассылка</h3>
            
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Заголовок</label>
              <input
                type="text"
                value={broadcast.title}
                onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })}
                placeholder="Важное объявление"
                style={{
                  width: '100%', padding: 12, borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)', color: '#fff',
                }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Сообщение</label>
              <textarea
                value={broadcast.message}
                onChange={(e) => setBroadcast({ ...broadcast, message: e.target.value })}
                placeholder="Текст рассылки..."
                rows={4}
                style={{
                  width: '100%', padding: 12, borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)', color: '#fff', resize: 'none',
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Кому</label>
              <select
                value={broadcast.target_role}
                onChange={(e) => setBroadcast({ ...broadcast, target_role: e.target.value })}
                style={{
                  width: '100%', padding: 12, borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)', color: '#fff',
                }}
              >
                <option value="">Всем пользователям</option>
                <option value="client">Только клиентам</option>
                <option value="manager">Только менеджерам</option>
                <option value="admin">Только админам</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={sendBroadcast}>
                📤 Отправить
              </button>
              <button className="btn" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={() => setShowBroadcast(false)}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, gap: 12, minHeight: 0 }}>
        {/* Chat List */}
        <div style={{
          width: selectedUser ? '35%' : '100%',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: 12,
          overflow: 'auto',
        }}>
          {chats.length === 0 ? (
            <div style={{ textAlign: 'center', opacity: 0.5, padding: 32 }}>
              Нет чатов
            </div>
          ) : (
            chats.map(c => (
              <div
                key={c.user_id}
                onClick={() => setSelectedUser(c)}
                style={{
                  padding: 12,
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  background: selectedUser?.user_id === c.user_id ? 'rgba(99,102,241,0.2)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 'bold' }}>{c.user_name || `User #${c.user_id}`}</div>
                  {c.unread > 0 && (
                    <span style={{
                      background: '#ef4444',
                      borderRadius: '50%',
                      width: 20, height: 20,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11,
                    }}>
                      {c.unread}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                  {c.last_message}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Chat Window */}
        {selectedUser && (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 12,
          }}>
            {/* Header */}
            <div style={{
              padding: 12,
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <div style={{ fontWeight: 'bold' }}>{selectedUser.user_name}</div>
                <div style={{ fontSize: 12, opacity: 0.6 }}>{selectedUser.user_role}</div>
              </div>
              <button
                className="btn btn-small"
                onClick={() => setSelectedUser(null)}
              >
                ✕
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
              {messages.map(m => (
                <div
                  key={m.id}
                  style={{
                    display: 'flex',
                    justifyContent: m.sender_type === 'admin' ? 'flex-end' : 'flex-start',
                    marginBottom: 8,
                  }}
                >
                  <div style={{
                    maxWidth: '80%',
                    padding: '10px 14px',
                    borderRadius: 12,
                    background: m.sender_type === 'admin'
                      ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                      : 'rgba(255,255,255,0.1)',
                  }}>
                    <div style={{ fontSize: 14 }}>{m.message}</div>
                    <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, textAlign: 'right' }}>
                      {formatTime(m.created_at)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendReply()}
                placeholder="Ответить..."
                style={{
                  flex: 1, padding: 12, borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)', color: '#fff',
                }}
              />
              <button
                className="btn btn-primary"
                onClick={sendReply}
                disabled={sending || !newMessage.trim()}
              >
                {sending ? '⏳' : '📤'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
